import React from 'react';
import styles from './PrivacyPolicy.module.css';

const PrivacyPolicy = () => {
  return (
    <div className={styles.privacyContainer}>
      <main className={styles.privacyContent}>
        <div className={styles.privacyHeader}>
          <h1>Privacy Policy</h1>
          <p className={styles.effectiveDate}>Effective Date: August 28, 2025 | Version: 1.0</p>
          <p className={styles.lastRevision}>Last Revised: August 28, 2025</p>
        </div>

        {/* Introduction */}
        <section id="introduction" className={styles.privacySection}>
          <h2>1. Introduction and Scope</h2>
          <p>
            This Privacy Policy ("Policy") describes how Rupayya Technologies Private Limited ("we", "us", "our", "Company") collects, uses, stores, shares, and protects your personal information when you use our peer-to-peer lending platform, website, mobile application, and related services (collectively, the "Platform").
          </p>
          <p>
            We are committed to protecting your privacy and handling your personal information with transparency and care. This Policy explains our data practices in compliance with applicable data protection laws including the Information Technology Act, 2000, Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, and other relevant regulations.
          </p>
          <p>
            By accessing or using the Platform, you acknowledge that you have read, understood, and agree to the collection and use of your personal information as described in this Policy. If you do not agree with our policies and practices, you must not access or use the Platform.
          </p>
          <div className={styles.importantNote}>
            <p><strong>Important:</strong> This Policy applies to all users of the Platform including lenders, borrowers, visitors, and any other individuals whose personal information we collect in connection with the Platform.</p>
          </div>
        </section>

        {/* Information Collection */}
        <section id="collection" className={styles.privacySection}>
          <h2>2. Information We Collect</h2>
          <p>
            We collect various types of information to provide and improve our services, comply with legal obligations, and protect the Platform. The information we collect falls into the following categories:
          </p>

          <h3>2.1 Personal Identification Information</h3>
          <div className={styles.infoTable}>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}><strong>Information Type</strong></div>
              <div className={styles.tableCell}><strong>Purpose</strong></div>
              <div className={styles.tableCell}><strong>Legal Basis</strong></div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Full Name</div>
              <div className={styles.tableCell}>Identity verification, KYC compliance, transaction documentation</div>
              <div className={styles.tableCell}>Contract necessity, legal obligation</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Date of Birth</div>
              <div className={styles.tableCell}>Age verification, regulatory compliance</div>
              <div className={styles.tableCell}>Legal obligation</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>PAN Number</div>
              <div className={styles.tableCell}>Tax compliance, identity verification, regulatory reporting</div>
              <div className={styles.tableCell}>Legal obligation</div>
            </div>
            <div className={styles.tableCell}>
              <div className={styles.tableCell}>Aadhaar Number</div>
              <div className={styles.tableCell}>Identity verification, address verification, KYC compliance</div>
              <div className={styles.tableCell}>Contract necessity, legal obligation</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Contact Information</div>
              <div className={styles.tableCell}>Communication, account management, transaction alerts</div>
              <div className={styles.tableCell}>Contract necessity, legitimate interest</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Photograph/Video</div>
              <div className={styles.tableCell}>Biometric verification, identity confirmation</div>
              <div className={styles.tableCell}>Contract necessity, legal obligation</div>
            </div>
          </div>

          <h3>2.2 Financial Information</h3>
          <ul>
            <li><strong>Bank Account Details:</strong> Account number, IFSC code, bank name, account type for transaction processing and verification</li>
            <li><strong>Transaction History:</strong> Details of all lending and borrowing transactions, including amounts, dates, parties, and terms</li>
            <li><strong>Payment Information:</strong> UPI IDs, payment gateway details, transaction references</li>
            <li><strong>Income Information:</strong> For enhanced verification and risk assessment where required</li>
            <li><strong>Tax Information:</strong> TDS details, GST numbers for business users</li>
          </ul>

          <h3>2.3 Device and Technical Information</h3>
          <ul>
            <li><strong>Device Information:</strong> Device type, operating system, browser type, screen resolution</li>
            <li><strong>IP Address:</strong> For security, fraud prevention, and geographic verification</li>
            <li><strong>Location Data:</strong> Approximate location from IP address or device settings (with consent)</li>
            <li><strong>Cookies and Tracking Technologies:</strong> As described in our Cookie Policy</li>
            <li><strong>Log Files:</strong> Access times, pages viewed, system activity, error reports</li>
          </ul>

          <h3>2.4 Usage and Behavioral Information</h3>
          <ul>
            <li><strong>Platform Interactions:</strong> Features used, time spent, navigation patterns</li>
            <li><strong>Communication Data:</strong> Messages, emails, chat logs with support</li>
            <li><strong>Preference Information:</strong> Language settings, notification preferences</li>
            <li><strong>Social Information:</strong> Relationship data for verification purposes</li>
          </ul>

          <h3>2.5 Sensitive Personal Information</h3>
          <div className={styles.importantNote}>
            <p><strong>Note:</strong> We treat the following as sensitive personal information under Indian law:</p>
            <ul>
              <li>Financial information including bank account details</li>
              <li>Biometric information (photographs, video recordings)</li>
              <li>Official identification numbers (PAN, Aadhaar)</li>
              <li>Authentication credentials</li>
            </ul>
            <p>We implement enhanced security measures for such information and collect it only when necessary for Platform operations or legal compliance.</p>
          </div>
        </section>

        {/* Collection Methods */}
        <section id="methods" className={styles.privacySection}>
          <h2>3. How We Collect Information</h2>
          <p>We collect information through various methods, including:</p>

          <h3>3.1 Information You Provide Directly</h3>
          <ul>
            <li><strong>Account Registration:</strong> Information provided during sign-up and profile creation</li>
            <li><strong>Verification Processes:</strong> Documents and information submitted for KYC verification</li>
            <li><strong>Transaction Initiation:</strong> Details provided when creating lending or borrowing agreements</li>
            <li><strong>Communication:</strong> Information shared when contacting support or other users</li>
            <li><strong>Surveys and Feedback:</strong> Responses to research and feedback requests</li>
          </ul>

          <h3>3.2 Information Collected Automatically</h3>
          <ul>
            <li><strong>Platform Usage:</strong> Data collected through cookies, web beacons, and similar technologies</li>
            <li><strong>Device Information:</strong> Automatic collection of technical data from your device</li>
            <li><strong>Transaction Monitoring:</strong> Automatic recording of all Platform transactions</li>
            <li><strong>Security Systems:</strong> Data collected by fraud detection and security systems</li>
          </ul>

          <h3>3.3 Information from Third Parties</h3>
          <ul>
            <li><strong>Verification Services:</strong> Information from government databases (UIDAI, Income Tax Department)</li>
            <li><strong>Payment Processors:</strong> Transaction status and confirmation from banks and payment gateways</li>
            <li><strong>Credit Bureaus:</strong> With user consent, for enhanced verification</li>
            <li><strong>Other Users:</strong> Information provided by other users during relationship verification</li>
            <li><strong>Public Sources:</strong> Information from publicly available sources for verification purposes</li>
          </ul>

          <h3>3.4 Special Collection Circumstances</h3>
          <div className={styles.importantNote}>
            <p><strong>Enhanced Due Diligence:</strong> For certain users (high-value transactions, suspicious activity, regulatory requirements), we may collect additional information including:</p>
            <ul>
              <li>Income proofs and salary slips</li>
              <li>Business registration documents</li>
              <li>Additional identity verification documents</li>
              <li>Source of funds documentation</li>
              <li>References and relationship proofs</li>
            </ul>
          </div>
        </section>

        {/* Use of Information */}
        <section id="use" className={styles.privacySection}>
          <h2>4. How We Use Your Information</h2>
          <p>We use your personal information for the following purposes:</p>

          <div className={styles.useTable}>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}><strong>Purpose</strong></div>
              <div className={styles.tableCell}><strong>Information Used</strong></div>
              <div className={styles.tableCell}><strong>Legal Basis</strong></div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Platform Operation</div>
              <div className={styles.tableCell}>Account information, contact details, preferences</div>
              <div className={styles.tableCell}>Contract necessity</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Identity Verification</div>
              <div className={styles.tableCell}>KYC documents, biometric data, identification numbers</div>
              <div className={styles.tableCell}>Legal obligation, contract necessity</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Transaction Processing</div>
              <div className={styles.tableCell}>Financial information, contact details, agreement terms</div>
              <div className={styles.tableCell}>Contract necessity</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Security and Fraud Prevention</div>
              <div className={styles.tableCell}>Device information, IP address, transaction patterns</div>
              <div className={styles.tableCell}>Legitimate interest, legal obligation</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Regulatory Compliance</div>
              <div className={styles.tableCell}>All relevant information as required by law</div>
              <div className={styles.tableCell}>Legal obligation</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Customer Support</div>
              <div className={styles.tableCell}>Contact information, transaction history, communication records</div>
              <div className={styles.tableCell}>Contract necessity, legitimate interest</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Platform Improvement</div>
              <div className={styles.tableCell}>Usage data, feedback, analytics (anonymized where possible)</div>
              <div className={styles.tableCell}>Legitimate interest</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Communication</div>
              <div className={styles.tableCell}>Contact information, preferences, transaction alerts</div>
              <div className={styles.tableCell}>Contract necessity, legitimate interest</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Dispute Resolution</div>
              <div className={styles.tableCell}>Transaction records, communication logs, verification data</div>
              <div className={styles.tableCell}>Contract necessity, legal obligation</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Legal Proceedings</div>
              <div className={styles.tableCell}>Relevant information as required by courts or authorities</div>
              <div className={styles.tableCell}>Legal obligation</div>
            </div>
          </div>

          <h3>4.1 Specific Use Cases</h3>
          <ul>
            <li><strong>Relationship Verification:</strong> Using location data, institutional information, and mutual declarations to verify user relationships</li>
            <li><strong>Risk Assessment:</strong> Analyzing transaction patterns and user behavior for risk scoring</li>
            <li><strong>Platform Personalization:</strong> Using preferences and usage patterns to customize user experience</li>
            <li><strong>Service Development:</strong> Analyzing aggregated data to develop new features and improve existing services</li>
            <li><strong>Marketing Communication:</strong> Sending relevant updates and information (with opt-out options)</li>
          </ul>
        </section>

        {/* Information Sharing */}
        <section id="sharing" className={styles.privacySection}>
          <h2>5. Information Sharing and Disclosure</h2>
          <p>We may share your information in the following circumstances:</p>

          <h3>5.1 With Other Users</h3>
          <div className={styles.importantNote}>
            <p><strong>Transaction Partners:</strong> When you engage in a transaction, we share necessary information with the other party, including:</p>
            <ul>
              <li>Name (as verified on Platform)</li>
              <li>Verified contact information (only for transaction communication)</li>
              <li>Transaction terms and agreement details</li>
              <li>Platform verification status</li>
              <li>Limited profile information relevant to the transaction</li>
            </ul>
            <p>We do not share sensitive personal information (bank details, identification numbers) between users without explicit consent.</p>
          </div>

          <h3>5.2 With Service Providers</h3>
          <div className={styles.providerTable}>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}><strong>Service Provider</strong></div>
              <div className={styles.tableCell}><strong>Information Shared</strong></div>
              <div className={styles.tableCell}><strong>Purpose</strong></div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Payment Processors</div>
              <div className={styles.tableCell}>Transaction details, bank information</div>
              <div className={styles.tableCell}>Payment processing and settlement</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Cloud Hosting Providers</div>
              <div className={styles.tableCell}>All Platform data (encrypted)</div>
              <div className={styles.tableCell}>Data storage and processing infrastructure</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Verification Services</div>
              <div className={styles.tableCell}>Identification documents, biometric data</div>
              <div className={styles.tableCell}>Identity and document verification</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Communication Services</div>
              <div className={styles.tableCell}>Contact information, message content</div>
              <div className={styles.tableCell}>Platform communications and notifications</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Analytics Providers</div>
              <div className={styles.tableCell}>Anonymized usage data</div>
              <div className={styles.tableCell}>Platform analytics and improvement</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Security Services</div>
              <div className={styles.tableCell}>Security logs, access patterns</div>
              <div className={styles.tableCell}>Fraud detection and security monitoring</div>
            </div>
          </div>

          <h3>5.3 Legal and Regulatory Disclosures</h3>
          <p>We may disclose your information when required by law or in response to:</p>
          <ul>
            <li><strong>Legal Requests:</strong> Court orders, subpoenas, or other legal processes</li>
            <li><strong>Regulatory Requirements:</strong> Requests from government authorities, regulatory bodies, or law enforcement agencies</li>
            <li><strong>Fraud Investigations:</strong> To financial institutions or authorities investigating fraud or financial crimes</li>
            <li><strong>Tax Authorities:</strong> Information required for tax compliance and reporting</li>
            <li><strong>AML/CFT Reporting:</strong> Suspicious transaction reports to Financial Intelligence Unit - India (FIU-IND)</li>
          </ul>

          <h3>5.4 Business Transfers</h3>
          <p>
            In the event of a merger, acquisition, reorganization, bankruptcy, or sale of assets, your information may be transferred as part of the transaction. We will notify you of any such change and outline your choices regarding your information.
          </p>

          <h3>5.5 With Your Consent</h3>
          <p>
            We may share your information with third parties when you provide explicit consent for specific purposes not covered elsewhere in this Policy.
          </p>
        </section>

        {/* Data Security */}
        <section id="security" className={styles.privacySection}>
          <h2>6. Data Security Measures</h2>
          <p>We implement comprehensive security measures to protect your information:</p>

          <h3>6.1 Technical Security Measures</h3>
          <div className={styles.securityGrid}>
            <div className={styles.securityItem}>
              <h4>Encryption</h4>
              <ul>
                <li>AES-256 encryption for data at rest</li>
                <li>TLS 1.3 encryption for data in transit</li>
                <li>End-to-end encryption for sensitive communications</li>
                <li>Tokenization for payment information</li>
              </ul>
            </div>
            <div className={styles.securityItem}>
              <h4>Access Controls</h4>
              <ul>
                <li>Role-based access control (RBAC)</li>
                <li>Multi-factor authentication for sensitive operations</li>
                <li>Principle of least privilege implementation</li>
                <li>Regular access reviews and audits</li>
              </ul>
            </div>
            <div className={styles.securityItem}>
              <h4>Network Security</h4>
              <ul>
                <li>Firewalls and intrusion detection systems</li>
                <li>DDoS protection and mitigation</li>
                <li>Regular vulnerability assessments</li>
                <li>Secure network architecture</li>
              </ul>
            </div>
            <div className={styles.securityItem}>
              <h4>Monitoring and Logging</h4>
              <ul>
                <li>24/7 security monitoring</li>
                <li>Comprehensive audit trails</li>
                <li>Real-time alerting for suspicious activities</li>
                <li>Regular security incident reviews</li>
              </ul>
            </div>
          </div>

          <h3>6.2 Organizational Security Measures</h3>
          <ul>
            <li><strong>Employee Training:</strong> Regular privacy and security training for all employees</li>
            <li><strong>Background Checks:</strong> Comprehensive vetting of employees with data access</li>
            <li><strong>Security Policies:</strong> Documented security policies and procedures</li>
            <li><strong>Vendor Management:</strong> Security assessments for third-party providers</li>
            <li><strong>Incident Response:</strong> Documented incident response plan and procedures</li>
          </ul>

          <h3>6.3 Compliance Certifications</h3>
          <div className={styles.complianceBadges}>
            <span className={styles.badge}>ISO 27001:2013 Certified</span>
            <span className={styles.badge}>PCI DSS Compliant</span>
            <span className={styles.badge}>GDPR Compliant Infrastructure</span>
            <span className={styles.badge}>RBI Security Guidelines Compliant</span>
          </div>

          <h3>6.4 Security Incident Response</h3>
          <p>
            In the event of a security incident involving your personal information, we will:
          </p>
          <ol>
            <li>Immediately investigate and contain the incident</li>
            <li>Notify affected users as required by law</li>
            <li>Report to relevant authorities if required</li>
            <li>Take steps to prevent recurrence</li>
            <li>Provide guidance to affected users</li>
          </ol>

          <div className={styles.importantNote}>
            <p><strong>User Responsibility:</strong> While we implement robust security measures, you also play a crucial role in protecting your information:</p>
            <ul>
              <li>Use strong, unique passwords for your account</li>
              <li>Enable two-factor authentication if available</li>
              <li>Keep your device security software updated</li>
              <li>Be cautious of phishing attempts</li>
              <li>Log out after using shared devices</li>
              <li>Report suspicious activity immediately</li>
            </ul>
          </div>
        </section>

        {/* Data Retention */}
        <section id="retention" className={styles.privacySection}>
          <h2>7. Data Retention Policy</h2>
          <p>We retain your personal information for as long as necessary to fulfill the purposes outlined in this Policy, unless a longer retention period is required or permitted by law.</p>

          <div className={styles.retentionTable}>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}><strong>Information Type</strong></div>
              <div className={styles.tableCell}><strong>Retention Period</strong></div>
              <div className={styles.tableCell}><strong>Legal Basis</strong></div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Account Information</div>
              <div className={styles.tableCell}>7 years after account closure</div>
              <div className={styles.tableCell}>Legal obligation, business necessity</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>KYC Documents</div>
              <div className={styles.tableCell}>5 years after account closure</div>
              <div className={styles.tableCell}>PMLA requirements</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Transaction Records</div>
              <div className={styles.tableCell}>7 years from transaction date</div>
              <div className={styles.tableCell}>Income Tax Act, RBI guidelines</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Communication Logs</div>
              <div className={styles.tableCell}>3 years</div>
              <div className={styles.tableCell}>Business necessity</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Security Logs</div>
              <div className={styles.tableCell}>2 years</div>
              <div className={styles.tableCell}>Security monitoring</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Marketing Data</div>
              <div className={styles.tableCell}>Until consent withdrawal</div>
              <div className={styles.tableCell}>User consent</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Backup Data</div>
              <div className={styles.tableCell}>30 days after deletion</div>
              <div className={styles.tableCell}>Disaster recovery</div>
            </div>
          </div>

          <h3>7.1 Data Deletion</h3>
          <p>
            Upon account closure or expiration of retention periods, we securely delete or anonymize your information. Deletion may be performed in phases due to technical constraints of backup systems.
          </p>

          <h3>7.2 Archive Purposes</h3>
          <p>
            Some information may be retained in archived form for:
          </p>
          <ul>
            <li>Legal claims or proceedings</li>
            <li>Regulatory investigations</li>
            <li>Historical research (anonymized)</li>
            <li>Platform security analysis</li>
          </ul>
        </section>

        {/* User Rights */}
        <section id="rights" className={styles.privacySection}>
          <h2>8. Your Rights and Choices</h2>
          <p>You have certain rights regarding your personal information:</p>

          <div className={styles.rightsGrid}>
            <div className={styles.rightItem}>
              <h4>Right to Access</h4>
              <p>You can request access to the personal information we hold about you, including details of how we use it and who we share it with.</p>
            </div>
            <div className={styles.rightItem}>
              <h4>Right to Correction</h4>
              <p>You can request correction of inaccurate or incomplete personal information. We may need to verify the accuracy of new information.</p>
            </div>
            <div className={styles.rightItem}>
              <h4>Right to Deletion</h4>
              <p>You can request deletion of your personal information, subject to legal and regulatory retention requirements.</p>
            </div>
            <div className={styles.rightItem}>
              <h4>Right to Object</h4>
              <p>You can object to certain processing activities, including direct marketing and processing based on legitimate interests.</p>
            </div>
            <div className={styles.rightItem}>
              <h4>Right to Restriction</h4>
              <p>You can request restriction of processing in certain circumstances, such as while disputing data accuracy.</p>
            </div>
            <div className={styles.rightItem}>
              <h4>Right to Portability</h4>
              <p>You can request a copy of your information in a structured, commonly used, machine-readable format.</p>
            </div>
            <div className={styles.rightItem}>
              <h4>Right to Withdraw Consent</h4>
              <p>Where processing is based on consent, you can withdraw consent at any time, without affecting lawfulness of prior processing.</p>
            </div>
            <div className={styles.rightItem}>
              <h4>Right to Complain</h4>
              <p>You have the right to lodge a complaint with relevant data protection authorities if you believe we have violated your rights.</p>
            </div>
          </div>

          <h3>8.1 How to Exercise Your Rights</h3>
          <p>
            To exercise your rights, contact our Data Protection Officer at dpo@rupayya.com. We will respond within 30 days and may request additional information to verify your identity.
          </p>

          <h3>8.2 Account Controls</h3>
          <p>
            You can also manage your information through account settings:
          </p>
          <ul>
            <li>Update contact information and preferences</li>
            <li>Manage notification settings</li>
            <li>View transaction history</li>
            <li>Download account data</li>
            <li>Close your account</li>
          </ul>

          <div className={styles.importantNote}>
            <p><strong>Limitations:</strong> Some rights may be limited by:</p>
            <ul>
              <li>Legal or regulatory requirements</li>
              <li>Ongoing contractual obligations</li>
              <li>Legitimate business interests</li>
              <li>Technical feasibility</li>
              <li>Need to verify identity</li>
            </ul>
          </div>
        </section>

        {/* International Transfers */}
        <section id="transfers" className={styles.privacySection}>
          <h2>9. International Data Transfers</h2>
          <p>
            We primarily store and process your information within India. However, some service providers may process data outside India. In such cases, we ensure appropriate safeguards:
          </p>

          <h3>9.1 Transfer Mechanisms</h3>
          <ul>
            <li><strong>Adequacy Decisions:</strong> Transfers to countries with adequate data protection laws</li>
            <li><strong>Standard Contractual Clauses:</strong> EU-approved clauses for transfers</li>
            <li><strong>Binding Corporate Rules:</strong> For transfers within corporate groups</li>
            <li><strong>Consent:</strong> Explicit consent for specific transfers</li>
          </ul>

          <h3>9.2 Countries of Processing</h3>
          <p>
            Your information may be processed in the following countries, each with specific safeguards:
          </p>
          <div className={styles.countryTable}>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}><strong>Country</strong></div>
              <div className={styles.tableCell}><strong>Purpose</strong></div>
              <div className={styles.tableCell}><strong>Safeguards</strong></div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>India</div>
              <div className={styles.tableCell}>Primary processing and storage</div>
              <div className={styles.tableCell}>Indian data protection laws</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>United States</div>
              <div className={styles.tableCell}>Cloud infrastructure, analytics</div>
              <div className={styles.tableCell}>Standard Contractual Clauses</div>
            </div>
            <div className={styles.tableRow}>
              <div className={styles.tableCell}>Singapore</div>
              <div className={styles.tableCell}>Regional backup, disaster recovery</div>
              <div className={styles.tableCell}>Adequacy decision</div>
            </div>
          </div>

          <h3>9.3 Government Access Requests</h3>
          <p>
            If we receive a request from a foreign government for your information, we will:
          </p>
          <ol>
            <li>Review the request for legal validity</li>
            <li>Notify you unless prohibited by law</li>
            <li>Challenge the request if appropriate</li>
            <li>Disclose only the minimum necessary information</li>
          </ol>
        </section>

        {/* Children's Privacy */}
        <section id="children" className={styles.privacySection}>
          <h2>10. Children's Privacy</h2>
          <p>
            Our Platform is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children under 18.
          </p>

          <div className={styles.importantNote}>
            <p><strong>Age Verification:</strong> We verify age during registration through date of birth and document verification. If we learn that we have collected personal information from a child under 18, we will:</p>
            <ol>
              <li>Immediately delete the information</li>
              <li>Terminate the account</li>
              <li>Notify the user (if contactable)</li>
              <li>Take steps to prevent recurrence</li>
            </ol>
          </div>

          <p>
            Parents or guardians who believe their child has provided personal information should contact us immediately at privacy@rupayya.com.
          </p>
        </section>

        {/* Cookies and Tracking */}
        <section id="cookies" className={styles.privacySection}>
          <h2>11. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar technologies as described in our separate <a href="#cookie-policy" className={styles.internalLink}>Cookie Policy</a>. Key points include:
          </p>

          <h3>11.1 Types of Cookies Used</h3>
          <div className={styles.cookieGrid}>
            <div className={styles.cookieType}>
              <h4>Essential Cookies</h4>
              <p>Required for Platform operation, cannot be disabled</p>
            </div>
            <div className={styles.cookieType}>
              <h4>Functional Cookies</h4>
              <p>Enhance user experience, can be disabled</p>
            </div>
            <div className={styles.cookieType}>
              <h4>Analytics Cookies</h4>
              <p>Help improve Platform, can be disabled</p>
            </div>
            <div className={styles.cookieType}>
              <h4>Marketing Cookies</h4>
              <p>Used for advertising, require consent</p>
            </div>
          </div>

          <h3>11.2 Control Options</h3>
          <p>
            You can control cookies through:
          </p>
          <ul>
            <li>Browser settings</li>
            <li>Platform cookie preferences</li>
            <li>Opt-out mechanisms for specific services</li>
            <li>Do Not Track signals (where supported)</li>
          </ul>

          <div className={styles.importantNote}>
            <p><strong>Impact of Disabling Cookies:</strong> Disabling certain cookies may affect Platform functionality, including:</p>
            <ul>
              <li>Login and session management</li>
              <li>Personalized features</li>
              <li>Transaction processing</li>
              <li>Security features</li>
            </ul>
          </div>
        </section>

        {/* Policy Updates */}
        <section id="updates" className={styles.privacySection}>
          <h2>12. Policy Updates and Changes</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors.
          </p>

          <h3>12.1 Update Process</h3>
          <ol>
            <li><strong>Review:</strong> Regular review of policy effectiveness and compliance</li>
            <li><strong>Revision:</strong> Updates based on legal changes, business needs, or user feedback</li>
            <li><strong>Approval:</strong> Internal review and approval process</li>
            <li><strong>Notification:</strong> Notification to users of material changes</li>
            <li><strong>Implementation:</strong> Effective date implementation</li>
          </ol>

          <h3>12.2 Notification Methods</h3>
          <p>
            We notify users of material changes through:
          </p>
          <ul>
            <li>Platform announcements and banners</li>
            <li>Email notifications to registered users</li>
            <li>Updated policy posting with clear effective date</li>
            <li>Summary of changes for significant updates</li>
          </ul>

          <h3>12.3 Your Continued Use</h3>
          <p>
            Your continued use of the Platform after updates constitutes acceptance of the revised Policy. If you do not agree with changes, you must stop using the Platform.
          </p>

          <div className={styles.updateHistory}>
            <h4>Recent Update History</h4>
            <ul>
              <li><strong>Version 2.0 (March 15, 2026):</strong> Coming with Comprehensive update for regulatory changes and full version of application</li>
              <li><strong>Version 1.0 (August 28, 2025):</strong> Initial policy implementation</li>
            </ul>
          </div>
        </section>

        {/* Contact Information */}
        <section id="contact" className={styles.privacySection}>
          <h2>13. Contact Information and Grievance Officer</h2>
          <p>For privacy-related inquiries, complaints, or to exercise your rights:</p>

          <div className={styles.contactGrid}>
            <div className={styles.contactItem}>
              <h4>Data Protection Officer</h4>
              <p>For data protection matters and rights requests</p>
              <p className={styles.contactDetail}>Email: support@rupayya.com</p>
              <p className={styles.contactDetail}>Phone: +91-8274087882</p>
            </div>
            <div className={styles.contactItem}>
              <h4>Grievance Officer</h4>
              <p>For complaints under Indian IT Rules</p>
              <p className={styles.contactDetail}>Email: grievance@rupayya.com</p>
              <p className={styles.contactDetail}>Address: As published on Platform</p>
            </div>
            <div className={styles.contactItem}>
              <h4>General Support</h4>
              <p>For general privacy questions</p>
              <p className={styles.contactDetail}>Email: privacy@rupayya.com</p>
              <p className={styles.contactDetail}>Platform: Help Center</p>
            </div>
          </div>

          <h3>13.1 Response Timeline</h3>
          <p>
            We aim to respond to privacy inquiries within:
          </p>
          <ul>
            <li><strong>General Inquiries:</strong> 7 business days</li>
            <li><strong>Rights Requests:</strong> 30 days (as required by law)</li>
            <li><strong>Complaints:</strong> 1 month for initial response</li>
          </ul>

          <h3>13.2 Regulatory Authorities</h3>
          <p>
            You also have the right to contact regulatory authorities:
          </p>
          <ul>
            <li>Ministry of Electronics and Information Technology</li>
            <li>Reserve Bank of India (for financial data matters)</li>
            <li>State data protection authorities (where applicable)</li>
          </ul>
        </section>

        {/* Final Provisions */}
        <div className={styles.finalProvisions}>
          <h2>Final Provisions</h2>
          <p>
            This Privacy Policy constitutes our complete statement of privacy practices for the Platform. It supersedes all previous versions and statements.
          </p>

          <div className={styles.acknowledgment}>
            <p><strong>By using our Platform, you acknowledge that:</strong></p>
            <ol>
              <li>You have read and understood this Privacy Policy</li>
              <li>You consent to our collection, use, and sharing of your information as described</li>
              <li>You understand your rights and how to exercise them</li>
              <li>You accept our security measures and limitations</li>
              <li>You agree to our data retention practices</li>
            </ol>
          </div>

          <div className={styles.legalInfo}>
            <p><strong>Applicable Laws:</strong> This Policy is governed by Indian law, particularly the Information Technology Act, 2000 and related rules.</p>
            <p><strong>Dispute Resolution:</strong> Privacy disputes shall be resolved through arbitration as per our Terms of Service.</p>
            <p><strong>Severability:</strong> If any provision is found invalid, the remaining provisions remain in effect.</p>
            <p className={styles.lastUpdatedFull}>Privacy Policy Version 1.0 | Effective August 28, 2025 | Comprehensive Data Protection Framework</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;